import { PriceStructure, QuotePrice } from '@brenger/api-client';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { HELP_ICON_AND_TITLE_TRANSKEY, useTypedSelector } from '../../../../../hooks';
import { floorAsString } from '../../../../../utils/floors';
import { getCarryHelpExtraCharge, getPriceStructure, getStopDetails, getStopHelp } from '../../../ducks';
import stairsDown from './icon-stairs-down.svg';
import stairsUp from './icon-stairs-up.svg';
import { TermsBlock } from './TermsBlock';
import { TermsPrice } from './TermsPrice';
import { StopType } from '../../../../../types';

export const TermsBlockService = (): JSX.Element => {
  const priceStructure = useTypedSelector(state => getPriceStructure(state));
  const pickupFloorCharge = getFloorCharge(priceStructure?.pickup);
  const deliveryFloorCharge = getFloorCharge(priceStructure?.delivery);
  const helpCharge = useTypedSelector(getCarryHelpExtraCharge);
  const help = useTypedSelector(getStopHelp);
  const pickupElevator = useTypedSelector(state => getStopDetails(state, StopType.PICKUP))?.elevator;
  const deliveryElevator = useTypedSelector(state => getStopDetails(state, StopType.DELIVERY))?.elevator;

  return (
    <div className={'pt-[1em]'}>
      <TermsBlock
        title={<Translate id={'request_flow.terms.block_heading.service'} />}
        borderRadius="top"
        border="bottom"
        titleClassName="main-title"
      />
      <TermsBlock spacingX={false} spacingY={false} borderRadius="bottom" className="order-overview--split">
        <div className={`order-overview--split-left icon--${help}`}>
          <TermsBlock
            title={<Translate id={HELP_ICON_AND_TITLE_TRANSKEY[help]?.titleKey} />}
            borderRadius="none"
            icon={HELP_ICON_AND_TITLE_TRANSKEY[help]?.icon}
          >
            <div className="flex justify-between" style={{ gap: '.5em' }}>
              <Translate id={HELP_ICON_AND_TITLE_TRANSKEY[help]?.explanationKey} />
              {helpCharge.incl_vat.amount !== 0 && <TermsPrice price={helpCharge} />}
            </div>
          </TermsBlock>
        </div>
        <div className="order-overview--split-right">
          <TermsBlock
            title={<Translate id={'request_flow.terms.block_heading.pickup'} />}
            border="bottom"
            borderRadius="none"
            icon={<img src={stairsDown} />}
          >
            {pickupElevator && !pickupFloorCharge && (
              <div className="flex justify-between">
                <div>
                  <Translate id={'request_flow.summary.elevator'} />
                </div>
                <TermsPrice price={zeroPrice} />
              </div>
            )}
            {pickupFloorCharge && (
              <div className="flex justify-between">
                <div>
                  {pickupFloorCharge.level && floorAsString(pickupFloorCharge.level) + ' '}
                  <Translate id={pickupFloorCharge.transKey} />
                </div>
                <TermsPrice price={pickupFloorCharge.price} />
              </div>
            )}
          </TermsBlock>
          <TermsBlock
            title={<Translate id={'request_flow.terms.block_heading.delivery'} />}
            borderRadius="none"
            icon={<img src={stairsUp} />}
          >
            {deliveryElevator && !deliveryFloorCharge && (
              <div className="flex justify-between">
                <div>
                  <Translate id={'request_flow.summary.elevator'} />
                </div>
                <TermsPrice price={zeroPrice} />
              </div>
            )}
            {deliveryFloorCharge && (
              <div className="flex justify-between">
                <div>
                  {deliveryFloorCharge.level && floorAsString(deliveryFloorCharge.level) + ' '}
                  <Translate id={deliveryFloorCharge.transKey} />
                </div>
                <TermsPrice price={deliveryFloorCharge.price} />
              </div>
            )}
          </TermsBlock>
        </div>
      </TermsBlock>
    </div>
  );
};

const getFloorCharge = (
  structure: PriceStructure[] | undefined
): null | { transKey: string; price: QuotePrice; level?: number } => {
  const addition = structure?.find(item => ['floor', 'elevator', 'basement'].includes(item.charge));

  if (!addition) return null;

  let transKey = `request_flow.summary.${addition.charge}`;
  let level = addition.charge === 'floor' ? addition.level : undefined;
  if (addition.charge === 'floor' && addition.level === 0) {
    transKey = 'request_flow.summary.ground_level';
    level = undefined;
  }
  if (addition.charge === 'basement') {
    transKey = 'request_flow.situation.x_basement_v2';
  }
  return {
    transKey,
    price: addition.price,
    level,
  };
};

const zeroPrice: QuotePrice = {
  vat: {
    amount: 0,
    currency: 'EUR',
  },
  incl_vat: {
    amount: 0,
    currency: 'EUR',
  },
  excl_vat: {
    amount: 0,
    currency: 'EUR',
  },
};
